import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";

const PERIODS = [
  { value: "30",  label: "Últimos 30 dias" },
  { value: "90",  label: "Últimos 90 dias" },
  { value: "180", label: "Últimos 180 dias" },
  { value: "365", label: "Último ano" },
];

function fmtDate(daysAgo) {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return d.toISOString().slice(0, 10);
}
function today() { return new Date().toISOString().slice(0, 10); }

export default function Intelligence() {
  const [searchParams] = useSearchParams();
  const initialCompany = searchParams.get("empresa") || "";
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState(initialCompany);
  const [period, setPeriod] = useState("90");
  const [alerts, setAlerts] = useState(null);
  const [correlation, setCorrelation] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);

  useEffect(() => {
    api.get("/companies").then((r) => {
      setCompanies(r.data);
      if (!companyId && r.data.length) setCompanyId(r.data[0].id);
    });
    // eslint-disable-next-line
  }, []);

  const range = () => ({ start: fmtDate(parseInt(period, 10)), end: today() });

  const loadAlerts = async () => {
    if (!companyId) return;
    const params = { company_id: companyId, ...range() };
    const r = await api.get("/absenteeism/alerts", { params });
    setAlerts(r.data);
  };
  const loadCorrelation = async () => {
    if (!companyId) return;
    const params = { company_id: companyId, ...range() };
    const r = await api.get("/absenteeism/nr01-correlation", { params });
    setCorrelation(r.data);
  };

  useEffect(() => {
    if (!companyId) return;
    setAnalysis(null);
    loadAlerts();
    loadCorrelation();
    // eslint-disable-next-line
  }, [companyId, period]);

  const runAnalysis = async (createPlans) => {
    if (!companyId) return;
    setAnalyzing(true);
    setAnalysis(null);
    try {
      const { start, end } = range();
      const r = await api.post("/absenteeism/analysis", {
        company_id: companyId, start, end, create_action_plans: !!createPlans,
      }, { timeout: 120000 });
      setAnalysis(r.data);
      if (createPlans) {
        toast.success(`${r.data.created_action_plan_ids.length} ação(ões) criadas no plano.`);
      } else {
        toast.success("Análise concluída.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="absenteeism-intelligence-page">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="overline">Absenteísmo Inteligente NR-01</div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900 mt-1">Inteligência preventiva</h1>
          <Link to="/app/absenteismo" className="text-sm text-emerald-800 hover:underline mt-2 inline-block">
            <i className="fa-solid fa-arrow-left mr-2"></i>Voltar ao painel
          </Link>
        </div>
        <Link to={`/app/absenteismo/planos?empresa=${companyId}`} className="btn btn-ghost border border-slate-300" data-testid="goto-plans">
          <i className="fa-solid fa-list-check"></i> Plano de ação
        </Link>
      </header>

      <div className="card flex flex-wrap items-end gap-3">
        <label className="flex-1 min-w-[220px]">
          <div className="overline mb-1">Empresa</div>
          <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} className="w-full border border-slate-300 px-3 py-2">
            {companies.map((c) => <option key={c.id} value={c.id}>{c.razao_social || c.nome_fantasia}</option>)}
          </select>
        </label>
        <label className="min-w-[180px]">
          <div className="overline mb-1">Período</div>
          <select value={period} onChange={(e) => setPeriod(e.target.value)} className="w-full border border-slate-300 px-3 py-2">
            {PERIODS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </label>
      </div>

      <AlertsCard alerts={alerts} />
      <CorrelationCard data={correlation} />
      <AnalysisCard analysis={analysis} analyzing={analyzing} onRun={runAnalysis} />
    </div>
  );
}

function AlertsCard({ alerts }) {
  if (!alerts) return <div className="card text-slate-500 text-sm">Carregando alertas…</div>;
  const sevTone = { high: "border-rose-300 bg-rose-50 text-rose-900", medium: "border-amber-300 bg-amber-50 text-amber-900", low: "border-slate-300 bg-slate-50" };
  const sevLabel = { high: "ALTA", medium: "MÉDIA", low: "BAIXA" };
  return (
    <div className="card" data-testid="alerts-card">
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="font-display text-xl font-extrabold">Alertas automáticos</h2>
        <div className="flex gap-2 text-xs">
          <span className="px-2 py-1 bg-rose-100 text-rose-900 border border-rose-300">{alerts.summary.high} ALTA</span>
          <span className="px-2 py-1 bg-amber-100 text-amber-900 border border-amber-300">{alerts.summary.medium} MÉDIA</span>
          <span className="px-2 py-1 bg-slate-100 text-slate-700 border border-slate-300">Total {alerts.summary.total}</span>
        </div>
      </div>
      {alerts.alerts.length === 0 ? (
        <div className="text-center py-8 text-slate-500">
          <i className="fa-solid fa-shield-check text-4xl mb-2 text-emerald-600"></i>
          <div className="text-sm">Nenhum alerta no período selecionado. Boa gestão!</div>
        </div>
      ) : (
        <div className="space-y-2">
          {alerts.alerts.map((a, i) => (
            <div key={i} className={`border-l-4 p-3 ${sevTone[a.severity] || sevTone.low}`} data-testid={`alert-row-${i}`}>
              <div className="flex items-baseline justify-between gap-3">
                <div className="font-bold text-sm">{a.title}</div>
                <span className="text-[10px] uppercase tracking-wider font-bold">{sevLabel[a.severity]}</span>
              </div>
              <div className="text-xs mt-1 leading-relaxed">{a.detail}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CorrelationCard({ data }) {
  if (!data) return null;
  if (!data.rows.length) return (
    <div className="card">
      <h2 className="font-display text-xl font-extrabold mb-3">Correlação Absenteísmo × NR-01</h2>
      <div className="text-sm text-slate-500 italic">Sem dados — colaboradores precisam ter GHE preenchido E avaliações NR-01 ativas para correlacionar.</div>
    </div>
  );
  return (
    <div className="card" data-testid="correlation-card">
      <h2 className="font-display text-xl font-extrabold mb-3">Correlação Absenteísmo × NR-01</h2>
      <p className="text-xs text-slate-600 mb-4">Cruzamento por GHE — destaque para grupos com <b>absenteísmo alto E risco psicossocial alto</b>.</p>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs uppercase tracking-wider text-slate-500 border-b">
            <th className="text-left py-2">GHE</th>
            <th className="text-right">Dias afast.</th>
            <th className="text-right">CID F</th>
            <th className="text-right">CID M</th>
            <th className="text-right">Score NR-01</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r) => (
            <tr key={r.ghe} className={`border-b border-slate-100 ${r.critical ? "bg-rose-50/60" : ""}`}>
              <td className="py-2 font-medium">{r.ghe}</td>
              <td className="text-right font-bold">{r.abs_days}</td>
              <td className="text-right">{r.cid_f}</td>
              <td className="text-right">{r.cid_m}</td>
              <td className="text-right">{r.nr01_score || "—"} <span className="text-[10px] text-slate-500">({r.nr01_responses} resp.)</span></td>
              <td>{r.critical
                ? <span className="text-xs px-2 py-0.5 bg-rose-200 text-rose-900 border border-rose-400">⚠ CRÍTICO</span>
                : <span className="text-xs text-slate-500">—</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AnalysisCard({ analysis, analyzing, onRun }) {
  return (
    <div className="card border-2 border-emerald-300 bg-emerald-50/30" data-testid="analysis-card">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="font-display text-xl font-extrabold">
          <i className="fa-solid fa-brain mr-2 text-emerald-700"></i>Análise inteligente (IA)
        </h2>
        {!analysis && (
          <button onClick={() => onRun(false)} disabled={analyzing} className="btn btn-primary disabled:opacity-50" data-testid="run-analysis-btn">
            {analyzing ? "Analisando…" : <><i className="fa-solid fa-wand-magic-sparkles mr-2"></i>Gerar análise com IA</>}
          </button>
        )}
      </div>
      {!analysis && !analyzing && (
        <p className="text-sm text-slate-600">
          A IA Gemini vai interpretar seus dados e gerar uma análise técnica em 3-5 parágrafos +
          plano de ação sugerido, com correlação aos fatores psicossociais NR-01 e ergonomia.
          <span className="block mt-1 text-xs italic">Dados pessoais (nome, CPF) <b>não</b> são enviados à IA.</span>
        </p>
      )}
      {analyzing && (
        <div className="py-8 text-center">
          <div className="inline-block animate-spin h-12 w-12 border-4 border-emerald-700 border-t-transparent mb-3"></div>
          <div className="text-sm text-slate-600">IA processando seus dados…</div>
        </div>
      )}
      {analysis && (
        <div className="space-y-4">
          <div className="space-y-3">
            {analysis.analise.map((p, i) => (
              <p key={i} className="text-sm leading-relaxed text-slate-800" data-testid={`analysis-para-${i}`}>{p}</p>
            ))}
          </div>
          {analysis.conclusao && (
            <div className="border-l-4 border-emerald-700 pl-4 py-2 bg-white/60">
              <div className="overline mb-1">Conclusão técnica</div>
              <p className="text-sm leading-relaxed">{analysis.conclusao}</p>
            </div>
          )}
          {analysis.plano_acao.length > 0 && (
            <div className="border-t border-slate-300 pt-4">
              <div className="flex items-baseline justify-between mb-3">
                <h3 className="font-display text-lg font-extrabold">Plano de ação sugerido pela IA</h3>
                <button onClick={() => onRun(true)} disabled={analyzing} className="btn btn-primary text-xs" data-testid="commit-action-plans">
                  <i className="fa-solid fa-check mr-1"></i>Adicionar todas ao plano
                </button>
              </div>
              <div className="space-y-2">
                {analysis.plano_acao.map((a, i) => (
                  <div key={i} className="border border-slate-200 bg-white p-3" data-testid={`ai-action-${i}`}>
                    <div className="flex items-baseline justify-between gap-3 mb-2">
                      <div className="font-bold text-sm">{a.problema}</div>
                      <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 ${
                        a.prioridade === "Crítica" ? "bg-rose-200 text-rose-900"
                        : a.prioridade === "Alta" ? "bg-amber-200 text-amber-900"
                        : "bg-slate-200 text-slate-700"}`}>{a.prioridade}</span>
                    </div>
                    {a.evidencia && <div className="text-xs text-slate-600 mb-1"><b>Evidência:</b> {a.evidencia}</div>}
                    {a.relacao_nr01 && <div className="text-xs text-slate-600 mb-1"><b>Relação NR-01:</b> {a.relacao_nr01}</div>}
                    <div className="text-sm mt-2"><b className="text-emerald-800">Ação:</b> {a.acao_recomendada}</div>
                    <div className="text-xs text-slate-500 mt-1">
                      {a.responsavel && <>Responsável: <b>{a.responsavel}</b></>}
                      {a.prazo && <> · Prazo: <b>{a.prazo}</b></>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
